import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, Percent, ArrowLeftRight, Copy, Check } from "lucide-react";
import useSeo from "../lib/useSeo";

const IVA_RATE = 0.19;

const fmtCLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(
    Math.round(n || 0),
  );

const parseAmount = (raw) => {
  if (raw === null || raw === undefined) return 0;
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return 0;
  return parseInt(digits, 10);
};

const groupInt = (n) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

export default function IvaCalculator() {
  useSeo({
    title: "Calculadora de IVA · Utilidades DoSoft",
    description:
      "Calculadora de IVA chileno (19%). Ingresa el monto neto o bruto y obtén al instante los tres valores.",
    canonical: "/utilidades/iva",
  });

  // noindex — internal tool.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  const [mode, setMode] = useState("neto"); // 'neto' | 'bruto'
  const [amountRaw, setAmountRaw] = useState("");
  const amount = parseAmount(amountRaw);

  const { neto, iva, bruto } = useMemo(() => {
    if (!amount) return { neto: 0, iva: 0, bruto: 0 };
    if (mode === "neto") {
      const _iva = Math.round(amount * IVA_RATE);
      return { neto: amount, iva: _iva, bruto: amount + _iva };
    }
    // bruto → back-calc neto
    const _neto = Math.round(amount / (1 + IVA_RATE));
    return { neto: _neto, iva: amount - _neto, bruto: amount };
  }, [amount, mode]);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copiado: ${fmtCLP(value)}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900 flex items-center gap-3">
            Calculadora de IVA
            <span className="text-xs md:text-sm font-mono font-normal bg-brand/15 text-brand-active px-2 py-1 rounded uppercase tracking-widest">
              19%
            </span>
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Ingresa el monto y elige si es neto o bruto. Los tres valores se completan automáticamente.
          </p>
        </div>

        <div className="rounded-2xl overflow-hidden border border-ink-500 bg-white shadow-lg">
          {/* Mode selector */}
          <div className="bg-ink-900 text-ash-100 px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest font-mono text-ash-500 mb-2">
              ¿Cómo quieres calcular el IVA?
            </div>
            <div className="flex gap-2">
              <ModeButton
                active={mode === "neto"}
                onClick={() => setMode("neto")}
                testid="iva-mode-neto"
              >
                Ingresar NETO
              </ModeButton>
              <ModeButton
                active={mode === "bruto"}
                onClick={() => setMode("bruto")}
                testid="iva-mode-bruto"
              >
                Ingresar BRUTO
              </ModeButton>
              {amount > 0 && (
                <button
                  onClick={() => setMode(mode === "neto" ? "bruto" : "neto")}
                  className="ml-auto h-11 inline-flex items-center gap-2 border border-ink-500 hover:border-brand hover:text-brand text-ash-300 px-3 rounded-lg text-xs uppercase tracking-widest font-mono"
                  title="Alternar entre neto y bruto"
                >
                  <ArrowLeftRight size={14} /> Alternar
                </button>
              )}
            </div>
          </div>

          {/* Big input */}
          <div className="p-6 md:p-8 border-b border-ash-100/60">
            <label className="block">
              <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">
                Monto {mode === "neto" ? "neto" : "bruto"} (CLP)
              </div>
              <div className="flex items-center border-2 border-brand rounded-xl px-5 py-4 focus-within:border-brand-active transition-colors">
                <span className="font-display font-black text-3xl md:text-4xl text-brand mr-3">$</span>
                <input
                  data-testid="iva-input"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  placeholder="0"
                  value={amountRaw ? groupInt(parseAmount(amountRaw)) : ""}
                  onChange={(e) => setAmountRaw(e.target.value)}
                  className="w-full font-display font-black text-3xl md:text-4xl text-ink-900 bg-transparent outline-none placeholder:text-ash-500/40"
                />
              </div>
            </label>
          </div>

          {/* Results row */}
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-ash-100/70">
            <ResultCell
              label="Valor Neto"
              value={neto}
              onCopy={() => copy("Neto", neto)}
              testid="iva-result-neto"
              highlight={mode === "neto"}
            />
            <ResultCell
              label={`IVA (${Math.round(IVA_RATE * 100)}%)`}
              value={iva}
              onCopy={() => copy("IVA", iva)}
              testid="iva-result-iva"
            />
            <ResultCell
              label="Total"
              sublabel="valor bruto"
              value={bruto}
              onCopy={() => copy("Total", bruto)}
              testid="iva-result-bruto"
              highlight={mode === "bruto"}
            />
          </div>
        </div>

        <div className="mt-8 text-xs text-ink-800/50 max-w-3xl">
          <strong>Nota:</strong> El IVA chileno equivale al 19% del valor neto. Fórmula: bruto = neto × 1,19 ·
          neto = bruto ÷ 1,19. Los montos se redondean al peso más cercano.
        </div>
      </main>
    </div>
  );
}

function ModeButton({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`h-11 px-5 rounded-lg text-xs uppercase tracking-widest font-mono transition-all ${
        active
          ? "bg-brand text-ink-900 font-semibold"
          : "border border-ink-500 hover:border-brand text-ash-300 hover:text-brand"
      }`}
    >
      {children}
    </button>
  );
}

function ResultCell({ label, sublabel, value, onCopy, testid, highlight }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      data-testid={testid}
      onClick={handle}
      className={`text-left p-5 md:p-6 hover:bg-ash-100/40 transition-colors group ${
        highlight ? "bg-brand/5" : ""
      }`}
      title="Click para copiar"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-mono text-ink-800/80 font-bold">
          {label}
        </span>
        {sublabel && (
          <span className="text-[10px] font-mono text-ink-800/50">({sublabel})</span>
        )}
        <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
          {copied ? (
            <Check size={12} className="text-[#0E6B2A]" strokeWidth={3} />
          ) : (
            <Copy size={12} className="text-ash-500" strokeWidth={1.8} />
          )}
        </span>
      </div>
      <div
        className={`font-display font-black text-2xl md:text-3xl leading-none ${
          value > 0 ? "text-ink-900" : "text-ash-500/60"
        }`}
      >
        {fmtCLP(value)}
      </div>
    </button>
  );
}
